import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Pencil, Plus, Trash2, Copy, TicketPercent } from "lucide-react";

type Promo = {
  id: string;
  codigo: string;
  descripcion: string | null;
  tipo: "porcentaje" | "fijo";
  valor: number;
  fecha_inicio: string | null;
  fecha_fin: string | null;
  usos_maximos: number | null;
  usos_actuales: number;
  minimo_noches: number | null;
  minimo_importe: number | null;
  activo: boolean;
};

const emptyPromo: Partial<Promo> = {
  codigo: "",
  descripcion: "",
  tipo: "porcentaje",
  valor: 10,
  activo: true,
};

export const Route = createFileRoute("/_authenticated/gestion/promociones")({
  component: PromocionesPage,
});

function PromocionesPage() {
  const [items, setItems] = useState<Promo[]>([]);
  const [editing, setEditing] = useState<Partial<Promo> | null>(null);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  async function load() {
    setLoading(true);
    const { data, error } = await supabase
      .from("codigos_promocionales")
      .select("*")
      .order("created_at", { ascending: false });
    if (error) toast.error(error.message);
    setItems(((data ?? []) as unknown) as Promo[]);
    setLoading(false);
  }
  useEffect(() => { load(); }, []);

  async function save() {
    if (!editing) return;
    const codigo = (editing.codigo ?? "").trim().toUpperCase();
    if (!codigo) return toast.error("Introduce un código");
    if (!editing.valor || Number(editing.valor) <= 0) return toast.error("Introduce un valor mayor que 0");
    const payload = {
      codigo,
      descripcion: editing.descripcion?.trim() || null,
      tipo: editing.tipo ?? "porcentaje",
      valor: Number(editing.valor),
      fecha_inicio: editing.fecha_inicio || null,
      fecha_fin: editing.fecha_fin || null,
      usos_maximos: editing.usos_maximos ? Number(editing.usos_maximos) : null,
      minimo_noches: editing.minimo_noches ? Number(editing.minimo_noches) : null,
      minimo_importe: editing.minimo_importe ? Number(editing.minimo_importe) : null,
      activo: editing.activo ?? true,
    };
    const q = editing.id
      ? supabase.from("codigos_promocionales").update(payload).eq("id", editing.id)
      : supabase.from("codigos_promocionales").insert(payload);
    const { error } = await q;
    if (error) return toast.error(error.message);
    toast.success("Guardado");
    setOpen(false);
    setEditing(null);
    load();
  }

  async function remove(id: string) {
    if (!confirm("¿Eliminar este código?")) return;
    const { error } = await supabase.from("codigos_promocionales").delete().eq("id", id);
    if (error) return toast.error(error.message);
    toast.success("Eliminado");
    load();
  }

  async function toggleActivo(p: Promo) {
    const { error } = await supabase.from("codigos_promocionales").update({ activo: !p.activo }).eq("id", p.id);
    if (error) return toast.error(error.message);
    load();
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-semibold"><TicketPercent className="h-6 w-6" /> Códigos promocionales</h1>
          <p className="text-sm text-muted-foreground">Descuentos aplicables desde el paso final del proceso de reserva público.</p>
        </div>
        <Button onClick={() => { setEditing({ ...emptyPromo }); setOpen(true); }}>
          <Plus className="mr-2 h-4 w-4" /> Nuevo código
        </Button>
      </div>

      {loading ? (
        <p className="text-sm text-muted-foreground">Cargando…</p>
      ) : items.length === 0 ? (
        <Card><CardContent className="py-10 text-center text-sm text-muted-foreground">
          Aún no tienes códigos promocionales. Crea el primero para ofrecer descuentos.
        </CardContent></Card>
      ) : (
        <div className="grid gap-3">
          {items.map((p) => {
            const caducado = p.fecha_fin ? new Date(p.fecha_fin) < new Date(new Date().toDateString()) : false;
            const agotado = p.usos_maximos != null && p.usos_actuales >= p.usos_maximos;
            return (
              <Card key={p.id}>
                <CardContent className="flex flex-wrap items-center justify-between gap-4 p-4">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="rounded-md bg-secondary px-2 py-1 font-mono text-sm font-semibold">{p.codigo}</span>
                      <Badge variant={p.activo && !caducado && !agotado ? "default" : "secondary"}>
                        {!p.activo ? "Desactivado" : caducado ? "Caducado" : agotado ? "Agotado" : "Activo"}
                      </Badge>
                      <span className="text-sm text-muted-foreground">
                        {p.tipo === "porcentaje" ? `${p.valor}% de descuento` : `${p.valor} € de descuento`}
                      </span>
                    </div>
                    {p.descripcion && <p className="mt-1 text-sm text-muted-foreground">{p.descripcion}</p>}
                    <p className="mt-1 text-xs text-muted-foreground">
                      {p.fecha_inicio || p.fecha_fin ? `Vigencia: ${p.fecha_inicio ?? "—"} → ${p.fecha_fin ?? "—"}` : "Sin fechas de vigencia"}
                      {" · "}Usos: {p.usos_actuales}{p.usos_maximos != null ? ` / ${p.usos_maximos}` : ""}
                      {p.minimo_noches ? ` · Mín. ${p.minimo_noches} noches` : ""}
                      {p.minimo_importe ? ` · Mín. ${p.minimo_importe} €` : ""}
                    </p>
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    <Switch checked={p.activo} onCheckedChange={() => toggleActivo(p)} />
                    <Button variant="ghost" size="icon" title="Copiar código" onClick={() => { navigator.clipboard.writeText(p.codigo); toast.success("Código copiado"); }}>
                      <Copy className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => { setEditing(p); setOpen(true); }}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => remove(p.id)}>
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setEditing(null); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editing?.id ? "Editar código" : "Nuevo código"}</DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="grid gap-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Código *</Label>
                  <Input
                    value={editing.codigo ?? ""}
                    onChange={(e) => setEditing({ ...editing, codigo: e.target.value.toUpperCase() })}
                    placeholder="VERANO25"
                    className="font-mono"
                  />
                </div>
                <div>
                  <Label>Tipo *</Label>
                  <Select value={editing.tipo ?? "porcentaje"} onValueChange={(v) => setEditing({ ...editing, tipo: v as "porcentaje" | "fijo" })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="porcentaje">Porcentaje (%)</SelectItem>
                      <SelectItem value="fijo">Importe fijo (€)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label>Valor *</Label>
                <Input type="number" step="0.01" min="0" value={editing.valor ?? ""} onChange={(e) => setEditing({ ...editing, valor: Number(e.target.value) })} />
              </div>
              <div>
                <Label>Descripción (opcional)</Label>
                <Input value={editing.descripcion ?? ""} onChange={(e) => setEditing({ ...editing, descripcion: e.target.value })} placeholder="Ej: Descuento verano 2026" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Válido desde</Label>
                  <Input type="date" value={editing.fecha_inicio ?? ""} onChange={(e) => setEditing({ ...editing, fecha_inicio: e.target.value })} />
                </div>
                <div>
                  <Label>Válido hasta</Label>
                  <Input type="date" value={editing.fecha_fin ?? ""} onChange={(e) => setEditing({ ...editing, fecha_fin: e.target.value })} />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label>Usos máximos</Label>
                  <Input type="number" min="1" value={editing.usos_maximos ?? ""} onChange={(e) => setEditing({ ...editing, usos_maximos: e.target.value ? Number(e.target.value) : null })} />
                </div>
                <div>
                  <Label>Mín. noches</Label>
                  <Input type="number" min="1" value={editing.minimo_noches ?? ""} onChange={(e) => setEditing({ ...editing, minimo_noches: e.target.value ? Number(e.target.value) : null })} />
                </div>
                <div>
                  <Label>Mín. importe (€)</Label>
                  <Input type="number" min="0" step="0.01" value={editing.minimo_importe ?? ""} onChange={(e) => setEditing({ ...editing, minimo_importe: e.target.value ? Number(e.target.value) : null })} />
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={editing.activo ?? true} onCheckedChange={(v) => setEditing({ ...editing, activo: v })} />
                <span className="text-sm">Activo</span>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => { setOpen(false); setEditing(null); }}>Cancelar</Button>
            <Button onClick={save}>Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}